import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { SelectionDispositifActions } from '@features/demande-financement/dispositifs/store/actions/selection-dispositif.actions';
import { AppActions } from 'src/app/app.action';
import { authFeature } from '@auth/store';
import { CreerDossier } from '@shared/models/dispositif/creer-dossier';
import { DispositifMin } from '@shared/models/dispositif/dispositif-min';
import { DispositifDataService } from '@features/demande-financement/dispositifs/service';
import { FolderActions } from '@features/dashboard/mon-stock/store/actions/folder.actions';
import { dispositifUrl } from '@shared/constants/link.constant';
import { selectAllSirets, selectIsQualiopi } from '@features/referentiel/features';
import { DialogService } from 'primeng/dynamicdialog';
import { InfoModalComponent } from '@shared/components/modal/info-modal/info-modal.component';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { RechercheForm } from '@features/mes-financements/features/mes-financements-recherche/models/recherche-form';
import { MesFinancementsFunctions } from '@features/mes-financements/features/mes-financements-recherche/functions/mes-financements-fonctions';
import { CategoryFolderEnum } from '@shared/enums/categorie.enum';
import { MesFinancementsService } from '@features/mes-financements/services/mes-finacements.service';
import { ModalConfirmationComponent } from '@shared/components/modal';
import { MesFinancementSearchFormActions } from '@features/mes-financements/store/search/search.actions';
import { MesFinancementsActions } from '@features/mes-financements/store/financements/mes-financements.actions';

@Injectable()
export class SelectionDispositifEffect {
  createDossier$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SelectionDispositifActions.createDispositifFinancement, AppActions.createDispositifFinancement, FolderActions.createDispositifFinancement),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.select(authFeature.selectUserSiret),
            this.store.select(selectIsQualiopi)
          ),
        ),
      ),
      concatMap(([{ dispositif }, siret, isQualiopi]) => {
        if (!isQualiopi) {
          this.dialogService.open(InfoModalComponent, {
            header: 'Information',
            closable: true,
            styleClass: 'custom-dialog',
            data: {
              text: this.translate.instant('dispositif.popup-message')
            }
          });
          return of(SelectionDispositifActions.noAction());
        } else {
          const creerDossier = new CreerDossier(
            new DispositifMin(dispositif?.code, dispositif?.name),
            siret
          );
          return this.dispositifDataService.createOpp(creerDossier).pipe(
            filter((result: any) => result && result.identifiantTechnique),
            map((result: any) =>
              SelectionDispositifActions.createDossierSuccess({
                dispositif,
                dossier: result,
              }),
            ),
            catchError((error) => of(SelectionDispositifActions.createDossierError({ error }))),
          );
        }
      }),
    ),
  );

  onDossierCreatedRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SelectionDispositifActions.createDossierSuccess),
        tap(({ dispositif, dossier }) => {
          const code =
            dispositif && dispositif.code ? dispositif.code.toLowerCase() : dispositif.code;
          this.router.navigate(['demande-financement', dispositifUrl[code], dossier.nomDossier, 'create'], {
            state: { numero: dossier.nomDossier, id: dossier.identifiantTechnique }
          });
        }),
      ),
    { dispatch: false },
  );


  createOrResumeDispositifFinancement$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppActions.createOrResumeDispositifFinancement),
      withLatestFrom(this.store.select(selectAllSirets)),
      switchMap(([action, sirets]) => {
        const form = new RechercheForm();
        form.typeDispositif = action.dispositif.id;
        const dossierSearchCriteria = MesFinancementsFunctions.searchFormToDossierSearchCriteria(sirets, form, CategoryFolderEnum.BROUILLON);
        return this.mesFinancementsService.getDossiers(dossierSearchCriteria).pipe(
          map((dossiers) => {
            if (dossiers.totalElements === 0) {
              return AppActions.createDispositifFinancement({ dispositif: action.dispositif });
            } else {
              const dialogRef = this.dialogService.open(ModalConfirmationComponent, {
                header: this.translate.instant('modal.use_draft.text'),
                closable: true,
                data: {
                  labelConfirm: this.translate.instant('modal.use_draft.buttons.new_request'),
                  labelCancel: this.translate.instant('modal.use_draft.buttons.resume_draft')
                },
              });
              dialogRef?.onClose?.subscribe((result: any) => {
                if (result) {
                  return this.store.dispatch(AppActions.createDispositifFinancement({ dispositif: action.dispositif }));
                }
                if (result === false) {
                  this.store.dispatch(MesFinancementSearchFormActions.displayDispositif({ value: true }));
                  this.store.dispatch(MesFinancementSearchFormActions.updateDispositif({
                    typeDispositif: action.dispositif.id!,
                    typeDispositifLibelle: action.dispositif.name,
                  }));
                  this.router.navigate(['mes-financements', 'brouillon']);
                }
                return;
              });
              return MesFinancementSearchFormActions.noActions();
            }
          }),
          catchError((error) =>
            of(
              MesFinancementsActions.getDossiersBySearchCriteriaFailure({
                errorMessage: error.error,
              }),
            ),
          ),
        );
      }),
    );
  },
  );
  constructor(
    private router: Router,
    private actions$: Actions,
    private store: Store,
    private dispositifDataService: DispositifDataService,
    private dialogService: DialogService,
    private translate: TranslateService,
    private mesFinancementsService: MesFinancementsService
  ) { }
}
